import { useState } from 'react';

import ReactJson, { type ThemeKeys } from '@microlink/react-json-view';
import { FlexAligner, Radio, RadioGroup, Mode as ThemeMode, Typography } from '@scaleaq/scaleui';
import { spacing } from '@scaleaq/sui-tokens/responsive';
import { useTranslation } from 'react-i18next';

import { asNonI18n } from '@/localization';
import { useUserSettingsStore } from '@/stores';

type CustomReactJsonProps<T> = {
  src: T;
  showThemeOptions?: boolean;
  collapsed?: number | boolean | undefined;
};

const REACT_JSON_THEME_DARK: ThemeKeys = 'ashes';
const REACT_JSON_THEME_LIGHT: ThemeKeys = 'bright:inverted';

/**
 * Custom ReactJson component to display JSON data with theme options.
 * Theme follows app setting (dark / light / auto = system preference). No live subscription to system changes.
 */
export const CustomReactJson = ({
  src,
  showThemeOptions = false,
  collapsed = 0,
}: CustomReactJsonProps<object>) => {
  const { theme: currentTheme } = useUserSettingsStore();
  const { t } = useTranslation(['common']);

  const themeFromApp = getThemeFromApp(currentTheme);
  const [reactJsonTheme, setReactJsonTheme] = useState<ThemeKeys>(themeFromApp);
  const displayTheme = showThemeOptions ? reactJsonTheme : themeFromApp;

  return (
    <>
      <FlexAligner style={{ overflow: 'auto' }} direction='column'>
        <Typography>{t('moreInformation')}</Typography>
        <ReactJson
          src={src || {}}
          name={false}
          displayDataTypes={false}
          enableClipboard={true}
          style={{
            padding: `${spacing['4xlarge']}`,
            width: '100%',
            border: '1px solid var(--sui-stroke-neutral-50)',
            borderRadius: '0.625rem',
          }}
          theme={displayTheme}
          iconStyle='circle'
          collapsed={collapsed}
          collapseStringsAfterLength={100}
        />
      </FlexAligner>

      {showThemeOptions && (
        <FlexAligner
          alignment='center-left'
          gap='2rem'
          style={{ paddingBottom: `${spacing['large']}` }}
        >
          <RadioGroup
            name='jsonViewerTheme'
            value={reactJsonTheme}
            onChange={theme => setReactJsonTheme(theme as ThemeKeys)}
          >
            <Radio id={'monokai'} value='monokai' label={asNonI18n('Monokai')}></Radio>
            <Radio id='twilight' value='twilight' label={asNonI18n('Twilight')}></Radio>
            <Radio id='ashes' value='ashes' label={asNonI18n('Ashes')}></Radio>
            <Radio id='harmonic' value='harmonic' label={asNonI18n('Harmonic')}></Radio>
            <Radio id='bright:inverted' value='bright:inverted' label={asNonI18n('Bright')}></Radio>
          </RadioGroup>
        </FlexAligner>
      )}
    </>
  );
};

function getThemeFromApp(currentTheme: ThemeMode): ThemeKeys {
  const isDark =
    currentTheme === 'dark' ||
    (currentTheme === 'auto' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);
  return isDark ? REACT_JSON_THEME_DARK : REACT_JSON_THEME_LIGHT;
}
